import moment from "moment";
import { showSuccessToast } from "~/components/Toast";
import i18n from "~/i18n";
import {
  invalidateTimeRecordFilters,
  invalidateTimeRecordTotalInfo,
  invalidateExpenseTotalInfo,
  invalidateExpenseFilters,
} from "~/repository/projectUtils";
import {
  EXPENSE_CREATED_FROM_EVENT_STREAM,
  EXPENSE_UPDATED_FROM_EVENT_STREAM,
  STOPWATCH_PAUSE_LOADING,
  STOPWATCH_PAUSE_SUCCESS,
  STOPWATCH_RESUME_LOADING,
  STOPWATCH_RESUME_SUCCESS,
  STOPWATCH_START_LOADING,
  STOPWATCH_START_SUCCESS,
  STOPWATCHES_FETCH_OFFSET_LOADING,
  STOPWATCHES_FETCH_OFFSET_SUCCESS,
  TIME_RECORD_CREATED_FROM_EVENT_STREAM,
  TIME_RECORD_UPDATED_FROM_EVENT_STREAM,
  STOPWATCH_UPDATED_FROM_EVENT_STREAM,
  ADD_TIME_RECORD_SUCCESS,
  DELETE_TIME_RECORD_SUCCESS,
  MOVE_TASK_TO_TRASH_SUCCESS,
  RESTORE_TASK_FROM_TRASH_SUCCESS,
  MOVE_TASK_LIST_TO_TRASH_SUCCESS,
  EDIT_TIME_RECORD_SUCCESS,
  ADD_EXPENSE_SUCCESS,
  EDIT_EXPENSE_SUCCESS,
  MOVE_EXPENSE_TO_TRASH_SUCCESS,
  TIME_RECORD_MOVE_TO_TRASH_FROM_EVENT_STREAM,
  EXPENSE_MOVE_TO_TRASH_FROM_EVENT_STREAM,
  EXPENSE_RESTORED_FROM_TRASH_FROM_EVENT_STREAM,
  TIME_RECORD_RESTORED_FROM_TRASH_FROM_EVENT_STREAM,
} from "~/store/action_types/index.jsx";

import { getStopwatchOffset } from "~/store/selectors/selector.additionalObjectData.jsx";
import { getLoggedUserId, makeGetUserDailyCapacitySelector } from "~/store/selectors/selector.users.jsx";
import { calculateElapsed, stopwatchMaximum } from "~/utils/date.jsx";
import { dispatchToElectron, sendStopwatchElectronMessage } from "~/utils/utils";
import { getConfigOptions } from "../actions/actions.configOptions.jsx";
import { loadTask } from "../actions/actions.tasks.jsx";

const trackingMiddleware =
  ({ dispatch, getState }) =>
  (next) =>
  (action) => {
    const { type, response } = action;

    switch (type) {
      case MOVE_TASK_TO_TRASH_SUCCESS:
      case RESTORE_TASK_FROM_TRASH_SUCCESS:
      case MOVE_TASK_LIST_TO_TRASH_SUCCESS: {
        const { single = {} } = response;
        const { project_id, parent_type, parent_id } = single;
        if (project_id) {
          invalidateTimeRecordFilters(project_id);
          invalidateExpenseFilters(project_id);
        }

        if (parent_type && parent_id) {
          invalidateTimeRecordTotalInfo(parent_type, parent_id);
          invalidateExpenseTotalInfo(parent_type, parent_id);
        }

        return next(action);
      }
      case ADD_TIME_RECORD_SUCCESS:
      case DELETE_TIME_RECORD_SUCCESS: {
        const { single = {} } = response;
        const { project_id, parent_type, parent_id } = single;
        if (project_id) {
          invalidateTimeRecordFilters(project_id);
        }

        if (parent_type && parent_id) {
          invalidateTimeRecordTotalInfo(parent_type, parent_id);
        }

        return next(action);
      }
      case ADD_EXPENSE_SUCCESS:
      case MOVE_EXPENSE_TO_TRASH_SUCCESS: {
        const { single = {} } = response;
        const { project_id, parent_type, parent_id } = single;
        if (project_id) {
          invalidateExpenseFilters(project_id);
        }

        if (parent_type && parent_id) {
          invalidateExpenseTotalInfo(parent_type, parent_id);
        }

        return next(action);
      }
      case EDIT_EXPENSE_SUCCESS: {
        const { single = {} } = response;
        const { parent_type, parent_id } = single;

        if (parent_type && parent_id) {
          invalidateExpenseTotalInfo(parent_type, parent_id);
        }

        return next(action);
      }
      case EDIT_TIME_RECORD_SUCCESS: {
        const { single = {} } = response;
        const { parent_type, parent_id } = single;

        if (parent_type && parent_id) {
          invalidateTimeRecordTotalInfo(parent_type, parent_id);
        }

        return next(action);
      }
      case TIME_RECORD_UPDATED_FROM_EVENT_STREAM:
      case EXPENSE_UPDATED_FROM_EVENT_STREAM:
      case TIME_RECORD_MOVE_TO_TRASH_FROM_EVENT_STREAM:
      case TIME_RECORD_RESTORED_FROM_TRASH_FROM_EVENT_STREAM:
      case EXPENSE_RESTORED_FROM_TRASH_FROM_EVENT_STREAM:
      case EXPENSE_MOVE_TO_TRASH_FROM_EVENT_STREAM:
      case TIME_RECORD_CREATED_FROM_EVENT_STREAM:
      case EXPENSE_CREATED_FROM_EVENT_STREAM: {
        if (response) {
          const { single = {} } = response;
          const { parent_type, parent_id, project_id } = single;
          if (parent_type === `Task`) {
            if (project_id && parent_id) {
              dispatch(loadTask(project_id, parent_id, true));
            }
          }
        }
        return next(action);
      }
      case STOPWATCHES_FETCH_OFFSET_LOADING: {
        action.request_start = moment().utc().unix();
        return next(action);
      }
      case STOPWATCHES_FETCH_OFFSET_SUCCESS: {
        action.request_end = moment().utc().unix();
        return next(action);
      }
      case STOPWATCH_START_LOADING:
      case STOPWATCH_RESUME_LOADING:
      case STOPWATCH_PAUSE_LOADING:
        action.request_offset = getStopwatchOffset(getState());
        return next(action);
      case STOPWATCH_START_SUCCESS: {
        const { single } = response;
        if (single.started_on) {
          sendStopwatchElectronMessage(0);
        }
        action.request_offset = getStopwatchOffset(getState());
        return next(action);
      }
      case STOPWATCH_RESUME_SUCCESS: {
        const { single } = response;
        const elapsed = calculateElapsed(single.started_on, single.elapsed, action.request_offset);
        sendStopwatchElectronMessage(elapsed);
        action.request_offset = getStopwatchOffset(getState());
        return next(action);
      }
      case STOPWATCH_PAUSE_SUCCESS: {
        dispatchToElectron("stopwatch-off", null);
        action.request_offset = getStopwatchOffset(getState());
        return next(action);
      }
      case STOPWATCH_UPDATED_FROM_EVENT_STREAM: {
        const { response = {} } = action;
        const { single: stopwatch = {} } = response;
        const { notification_sent_at, elapsed, started_on } = stopwatch;

        if (notification_sent_at) {
          dispatch(getConfigOptions(`user_daily_capacity`)).then(() => {
            const selectorDailyCapacity = makeGetUserDailyCapacitySelector(getLoggedUserId(getState()));
            const dailyCapacity = selectorDailyCapacity(getState());
            const message = i18n.t("Stopwatch has been running over your daily capacity");
            showSuccessToast(`${message} (${dailyCapacity}h)`);
          });
        }

        if (!started_on && elapsed >= stopwatchMaximum) {
          showSuccessToast(i18n.t("The Stopwatch has reached the time limit!"));
        }

        return next(action);
      }
      default:
        return next(action);
    }
  };

export default trackingMiddleware;
